import { ConnectionListResult, FeatureFlagsContext, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { FlowsDisplayGridRowDataFragment } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragment';
import { FlowsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  TableDownstreamFlowsFilteredByColumnQuery,
  TableDownstreamFlowsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableDownstreamFlowsFilteredByColumnQuery';
import {
  TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQuery,
  TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQuery';
import { TableDownstreamFlowsQuery, TableDownstreamFlowsQueryVariables } from '../../../gen-src/queries/TableDownstreamFlowsQuery';
import { TableDownstreamFlowsWithoutPersonalSpaceQuery, TableDownstreamFlowsWithoutPersonalSpaceQueryVariables } from '../../../gen-src/queries/TableDownstreamFlowsWithoutPersonalSpaceQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableDownstreamFlowsFilteredByColumnQueryGql } from '../../queries/TableDownstreamFlowsFilteredByColumnQueryGql';
import { TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryGql';
import { TableDownstreamFlowsQueryGql } from '../../queries/TableDownstreamFlowsQueryGql';
import { TableDownstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamFlowsWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = TableDownstreamFlowsQueryVariables;
type QueryResults = TableDownstreamFlowsQuery;
type QueryResultListNode = FlowsDisplayGridRowDataFragment;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = TableDownstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = TableDownstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = TableDownstreamFlowsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamFlowsFilteredByColumnQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.downstreamFlowsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.downstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.downstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.downstreamFlowsConnection;
}

export type TableDownstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly tableId: string;
  readonly filteredColumnId: string | null;
  readonly rowCount: number | null;
};

export function TableDownstreamFlowsQueryGrid(props: TableDownstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={TableDownstreamFlowsFilteredByColumnQueryGql}
        partialQueryVariables={{ tableId: props.tableId, columnId: props.filteredColumnId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={TableDownstreamFlowsQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  ) : (
      props.filteredColumnId ? (
        <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={TableDownstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{ tableId: props.tableId, columnId: props.filteredColumnId, orderBy: props.orderBy as FlowSortOrder }}
          selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
          mapResultNode={getRowDataForFlowWithoutPersonalSpace}
        >
          {({ rowStore }) => (
            <FlowsDisplayGrid
              rowStore={rowStore}
              emptyGridMessage={emptyFilteredGridMessage}
              rowCount={props.rowCount}
              orderBy={props.orderBy}
              updateSortOrder={props.updateSortOrder}
            />
          )}
        </PaginatedQueryWrapper>
      ) : (
        <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={TableDownstreamFlowsWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as FlowSortOrder }}
          selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
          mapResultNode={getRowDataForFlowWithoutPersonalSpace}
        >
          {({ rowStore }) => (
            <FlowsDisplayGrid
              rowStore={rowStore}
              emptyGridMessage={emptyGridMessage}
              rowCount={props.rowCount}
              orderBy={props.orderBy}
              updateSortOrder={props.updateSortOrder}
            />
          )}
        </PaginatedQueryWrapper>
      )
    );
}
